// hooks/useOrganization.js
import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, updateDoc, setDoc, arrayUnion, arrayRemove, writeBatch } from 'firebase/firestore';
import { db } from '../firebase/config';

export function useOrganization(organizationId, userId, userData) {
  const [organizationData, setOrganizationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!organizationId) {
      setOrganizationData(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    const unsubscribe = onSnapshot(
      doc(db, 'organizations', organizationId),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          setOrganizationData({ id: docSnapshot.id, ...docSnapshot.data() });
        } else {
          setOrganizationData(null);
        }
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching organization data:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [organizationId]);

  const updateOrganization = useCallback(async (updates) => {
    if (!organizationId) throw new Error('No organization ID available');
    try {
      await updateDoc(doc(db, 'organizations', organizationId), {
        ...updates,
        updatedAt: new Date(),
      });
      return { success: true };
    } catch (err) {
      console.error('Error updating organization:', err);
      throw err;
    }
  }, [organizationId]);

  const createOrganization = useCallback(async (organizationData) => {
    if (!userId) throw new Error('User must be authenticated to create an organization');
    try {
      const orgRef = doc(db, 'organizations', userId);
      const creatorMember = {
        userId,
        name: userData?.firstName && userData?.lastName
          ? `${userData.firstName} ${userData.lastName}`
          : '',
        email: userData?.email || '',
        phone: userData?.phone || '',
        role: 'admin',
        memberType: 'any',
      };
      await setDoc(orgRef, {
        ...organizationData,
        ownerId: userId,
        members: [creatorMember],
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      await updateDoc(doc(db, 'users', userId), {
        organizationId: userId,
        organizationName: organizationData.name,
        organizationRole: 'owner',
      });
      return { success: true, organizationId: userId };
    } catch (err) {
      console.error('Error creating organization:', err);
      throw err;
    }
  }, [userId, userData]);

  const addMember = useCallback(async (memberData) => {
    if (!organizationId) throw new Error('No organization ID available');
    if (!memberData?.userId) throw new Error('Käyttäjätunnus (UID) puuttuu');

    // Prevent duplicate members — arrayUnion only dedupes identical objects,
    // so the same userId with different fields would be added twice.
    const alreadyMember = organizationData?.members?.some(
      (m) => m.userId === memberData.userId
    );
    if (alreadyMember) throw new Error('Käyttäjä on jo organisaation jäsen');

    // Firestore rejects undefined values — use null for empty optional fields.
    const newMember = {
      userId:     memberData.userId,
      name:       memberData.name       ?? null,
      email:      memberData.email      ?? null,
      phone:      memberData.phone      ?? null,
      memberType: memberData.memberType || 'any',
      role:       memberData.role       || 'regular',
      payment:    memberData.payment    ?? null,
      billing:    memberData.billing    ?? null,
    };

    try {
      await updateDoc(doc(db, 'organizations', organizationId), {
        members: arrayUnion(newMember),
        updatedAt: new Date(),
      });
      return { success: true };
    } catch (err) {
      console.error('Error adding member:', err);
      throw err;
    }
  }, [organizationId, organizationData]);

  const removeMember = useCallback(async (memberUserId) => {
    if (!organizationId) throw new Error('No organization ID available');
    try {
      const memberToRemove = organizationData?.members?.find(m => m.userId === memberUserId);
      if (!memberToRemove) throw new Error('Member not found');

      await updateDoc(doc(db, 'organizations', organizationId), {
        members: arrayRemove(memberToRemove),
        updatedAt: new Date(),
      });
      return { success: true };
    } catch (err) {
      console.error('Error removing member:', err);
      throw err;
    }
  }, [organizationId, organizationData]);

  // Update a member's fields in the members array.
  // Firestore doesn't support updating individual array elements, so we
  // remove the old object and add the merged replacement in a batch.
  const updateMember = useCallback(async (memberUserId, updatedFields) => {
    if (!organizationId) throw new Error('No organization ID available');

    const existingMember = organizationData?.members?.find(m => m.userId === memberUserId);
    if (!existingMember) throw new Error('Member not found');

    const updatedMember = { ...existingMember, ...updatedFields };

    try {
      const orgRef = doc(db, 'organizations', organizationId);
      // arrayRemove + arrayUnion must be separate updates — batch them
      const batch = writeBatch(db);
      // Step 1: remove old
      batch.update(orgRef, { members: arrayRemove(existingMember) });
      // Step 2: add updated — Firestore batches are sequential so this is safe
      batch.update(orgRef, {
        members: arrayUnion(updatedMember),
        updatedAt: new Date(),
      });
      await batch.commit();
      return { success: true };
    } catch (err) {
      console.error('Error updating member:', err);
      throw err;
    }
  }, [organizationId, organizationData]);

  const isOwner = useCallback(() => {
    if (!userId || !organizationData) return false;
    return organizationData.ownerId === userId;
  }, [userId, organizationData]);

  const isMember = useCallback(() => {
    if (!userId || !organizationData) return false;
    return organizationData.members?.some(m => m.userId === userId) || false;
  }, [userId, organizationData]);

  const updateDeliveryPaymentStatus = useCallback(async (deliveryId, status = 'completed') => {
    if (!organizationId) throw new Error('No organization ID available');
    try {
      await updateDoc(doc(db, 'organizations', organizationId, 'deliveries', deliveryId), {
        paymentStatus: { status, time: Date.now() },
      });
      return { success: true };
    } catch (err) {
      console.error('Error updating payment status:', err);
      throw err;
    }
  }, [organizationId]);

  const updateDeliveryBillingStatus = useCallback(async (deliveryId, status = 'completed') => {
    if (!organizationId) throw new Error('No organization ID available');
    try {
      await updateDoc(doc(db, 'organizations', organizationId, 'deliveries', deliveryId), {
        billingStatus: { status, time: Date.now() },
      });
      return { success: true };
    } catch (err) {
      console.error('Error updating billing status:', err);
      throw err;
    }
  }, [organizationId]);

  const updateDeliveryStatuses = useCallback(async (deliveryId, updates) => {
    if (!organizationId) throw new Error('No organization ID available');
    try {
      const now = Date.now();
      const statusUpdates = {};
      if (updates.payment) statusUpdates.paymentStatus = { status: 'completed', time: now };
      if (updates.billing) statusUpdates.billingStatus = { status: 'completed', time: now };
      await updateDoc(doc(db, 'organizations', organizationId, 'deliveries', deliveryId), statusUpdates);
      return { success: true };
    } catch (err) {
      console.error('Error updating delivery statuses:', err);
      throw err;
    }
  }, [organizationId]);

  const bulkUpdateDeliveryStatuses = useCallback(async (deliveryIds, action) => {
    if (!organizationId) throw new Error('No organization ID available');
    try {
      const batch = writeBatch(db);
      const now = Date.now();
      deliveryIds.forEach(deliveryId => {
        const ref = doc(db, 'organizations', organizationId, 'deliveries', deliveryId);
        const updates = {};
        if (action === 'paid'   || action === 'both') updates.paymentStatus = { status: 'completed', time: now };
        if (action === 'billed' || action === 'both') updates.billingStatus = { status: 'completed', time: now };
        batch.update(ref, updates);
      });
      await batch.commit();
      return { success: true };
    } catch (err) {
      console.error('Error bulk updating delivery statuses:', err);
      throw err;
    }
  }, [organizationId]);

  return {
    organizationData,
    loading,
    error,
    organizationId,
    updateOrganization,
    createOrganization,
    addMember,
    removeMember,
    updateMember,
    isOwner,
    isMember,
    hasOrganization: !!organizationId,
    updateDeliveryPaymentStatus,
    updateDeliveryBillingStatus,
    updateDeliveryStatuses,
    bulkUpdateDeliveryStatuses,
    getName:        () => organizationData?.name        || '',
    getDescription: () => organizationData?.description || '',
    getLocation:    () => organizationData?.location    || null,
    getMembers:     () => organizationData?.members     || [],
    getMemberCount: () => organizationData?.members?.length || 0,
  };
}